import { BitkitList, IconCheck, IconValidateShield, useResponsive } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Separator } from '@chakra-ui/react/separator';
import { Text } from '@chakra-ui/react/text';
import React from 'react';

import { ProductIntent, productIntentConfigurations } from '../../../pages/SignUp/SignUpPage/useProductIntent';
import AlertSlot from '../../AlertSlot/AlertSlot';
import PageContent from '../PageContent/PageContent';
import BitbotLogoWithText from '../PageHeader/PageHeaderContent/BitbotLogoWithText';
import PublicPage from '../PublicPage';
import AuthPageBackground from './AuthPageBackground';

const AuthPage = ({
  title,
  subtitle,
  bottomMessage,
  productIntent,
  children,
}: {
  title: string;
  subtitle?: string;
  bottomMessage: React.ReactNode;
  productIntent?: ProductIntent;
  children: React.ReactNode;
}) => {
  const { isMobile } = useResponsive();

  const productIntentConfiguration = productIntent ? productIntentConfigurations[productIntent] : undefined;

  const gridProps = productIntentConfiguration
    ? {
        gridTemplateColumns: ['1fr', '1fr 1fr'],
        gridTemplateRows: ['auto auto auto', 'auto 1fr'],
        gridTemplateAreas: [
          `"top"
           "form"
           "bottom"`,
          `"top form"
           "top bottom"`,
        ],
        gap: '24px 0',
      }
    : {
        gridTemplateColumns: ['1fr', 'auto 420px'],
        gridTemplateRows: ['auto auto auto', 'auto 1fr'],
        gridTemplateAreas: [
          `"top"
           "form"
           "bottom"`,
          `"top form"
           "bottom form"`,
        ],
        gap: '24px 88px',
      };

  const topContent = productIntentConfiguration ? (
    <Box
      display="flex"
      flexDirection="column"
      alignItems={['center', 'flex-start']}
      gap="24"
      width="100%"
      maxWidth="392px"
      paddingX="16px"
      paddingBottom={['32px', '16px']}
      background={['background/primary', 'color/white/a90']}
      borderRadius="8"
    >
      <BitbotLogoWithText height={40} colorScheme="purple" />
      <Text
        as="h1"
        fontSize={['1.875rem', '2.25rem']}
        fontWeight="700"
        lineHeight={['2.5rem', '3rem']}
        color="text/primary"
        textAlign={['center', 'left']}
        css={{ textWrap: 'balance' }}
      >
        {productIntentConfiguration.title}{' '}
        <Text as="span" color="color/purple/strong">
          {productIntentConfiguration.name}
        </Text>
      </Text>
      <Box width="100%">
        <BitkitList>
          {productIntentConfiguration.features.map((feature) => (
            <BitkitList.Item key={feature} icon={IconCheck} iconColor="icon/positive">
              {feature}
            </BitkitList.Item>
          ))}
        </BitkitList>
      </Box>
      {productIntentConfiguration.paymentNotice && (
        <Box width="100%">
          <Separator borderColor="border/minimal" />
          <Box display="flex" alignItems="flex-start" gap="8" paddingLeft="4" paddingTop="12">
            <IconValidateShield size="16" color="icon/tertiary" marginTop="2" />
            <Text textStyle="body/md/regular" color="text/secondary" css={{ textWrap: 'balance' }}>
              {productIntentConfiguration.paymentNotice}
            </Text>
          </Box>
        </Box>
      )}
    </Box>
  ) : (
    <Box
      display="flex"
      flexDirection="column"
      alignItems={['center', 'flex-start']}
      gap="24"
      paddingX={['16px', '0']}
      paddingTop={['2rem', '4rem']}
    >
      <BitbotLogoWithText height={isMobile ? 32 : 41} />
      <Box display="flex" flexDirection="column" gap={['4', '12']}>
        <Text
          as="h1"
          fontSize={['1.5rem', '3rem']}
          fontWeight="700"
          lineHeight={['2.25rem', '3.75rem']}
          color="text/on-color"
          textAlign={['center', 'left']}
          css={{ textWrap: 'balance' }}
        >
          {title}
        </Text>
        {subtitle && (
          <Text
            textStyle={isMobile ? 'body/md/regular' : 'body/xl/regular'}
            color="text/on-color"
            css={{ textWrap: 'balance' }}
            textAlign={['center', 'left']}
          >
            {subtitle}
          </Text>
        )}
      </Box>
    </Box>
  );

  return (
    <PublicPage>
      <AuthPageBackground productIntentIllustration={productIntentConfiguration?.illustration}>
        <PageContent
          paddingX="0"
          paddingTop={[0, '3.75rem']}
          position="relative"
          {...(productIntentConfiguration && { maxWidth: '1320px', width: '100%' })}
        >
          <Box paddingX="16">
            <AlertSlot marginBlock="16" />
          </Box>

          <Box display="grid" alignItems="center" {...gridProps} width="100%">
            <Box
              gridArea="top"
              alignSelf="start"
              gap="16"
              display="flex"
              flexDirection="column"
              alignItems="center"
              marginTop={['0', '36px']}
            >
              {topContent}
            </Box>

            <Box
              gridArea="form"
              gap="16"
              display="flex"
              flexDirection="column"
              alignItems="center"
              paddingX={['16px', '0']}
              marginTop={['0', '36px']}
            >
              {children}
            </Box>

            <Box gridArea="bottom" alignSelf="start">
              {/* Bottom content */}
              <Text
                textStyle="body/lg/regular"
                color="text/on-color"
                textAlign={['center', productIntentConfiguration ? 'center' : 'left']}
              >
                {bottomMessage}
              </Text>
            </Box>
          </Box>
        </PageContent>
      </AuthPageBackground>
    </PublicPage>
  );
};

export default AuthPage;
